// =============================================================================
// FILE:
// src/modules/pms/task_material/components/MaterialAllocationDrawer.js
// =============================================================================

import React, { useEffect, useState } from "react";

import {

  getTaskBOMMaterials,

  allocateBOMMaterialsToTask

} from "../../services/taskMaterialService";


// =============================================================================
// COMPONENT
// =============================================================================

export default function MaterialAllocationDrawer({

  open,

  onClose,

  taskNode,

  onSuccess

}) {

  // ===========================================================================
  // STATE
  // ===========================================================================
  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [bomMaterials, setBomMaterials] = useState([]);

  const [selectedRows, setSelectedRows] = useState({});

  const [searchText, setSearchText] = useState("");

  const [statusFilter, setStatusFilter] =   useState("ALL");

  // ===========================================================================
  // LOAD BOM MATERIALS
  // ===========================================================================
  const loadBOMMaterials = async () => {

    if (!taskNode?.id) {
      return;
    }

    try {

      setLoading(true);

      const res = await getTaskBOMMaterials(
        taskNode.id
      );

      const rows = res.data.data || [];

      setBomMaterials(rows);

    } catch (err) {

      console.error(
        "Error loading BOM materials",
        err
      );

      alert(
        err?.response?.data?.message ||
        "Failed to load BOM materials"
      );

      setBomMaterials([]);

    } finally {

      setLoading(false);
    }
  };

  // ===========================================================================
  // OPEN EFFECT
  // ===========================================================================
  useEffect(() => {

    if (open) {

      setSelectedRows({});

      loadBOMMaterials();
    }

  }, [open]);

  // ===========================================================================
  // TOGGLE ROW
  // ===========================================================================
  const toggleRow = (row) => {

    setSelectedRows((prev) => {

      const updated = { ...prev };

      if (updated[row.bom_line_id]) {

        delete updated[row.bom_line_id];

      } else {

        updated[row.bom_line_id] = {

          ...row,

          planned_qty: row.bom_qty || 0
        };
      }

      return updated;
    });
  };

  // ===========================================================================
  // UPDATE QTY
  // ===========================================================================
  const updateQty = (
    bomLineId,
    value
  ) => {

    setSelectedRows((prev) => ({

      ...prev,

      [bomLineId]: {

        ...prev[bomLineId],

        planned_qty: value
      }

    }));
  };

  // ===========================================================================
  // SAVE
  // ===========================================================================
  const handleAllocate = async () => {

    const rows = Object.values(
      selectedRows
    );

    if (rows.length === 0) {

      alert(
        "Please select at least one material"
      );

      return;
    }

    try {

      setSaving(true);

      await allocateBOMMaterialsToTask(

        taskNode.id,

        rows

      );

      alert(
        "Materials allocated successfully"
      );

      if (onSuccess) {
        await onSuccess();
      }

      onClose();

    } catch (err) {

      alert(
        err?.response?.data?.message ||
        "Allocation failed"
      );

    } finally {

      setSaving(false);
    }
  };

  // ===========================================================================
  // CLOSED
  // ===========================================================================
  if (!open) {
    return null;
  }

  
  const filteredRows = bomMaterials.filter((row) => {

  const search = searchText.toLowerCase();

  // ---------------------------------------------------------
  // SEARCH MATCH
  // ---------------------------------------------------------
  const matchesSearch = (

    (row.material_code || "")
      .toLowerCase()
      .includes(search)

    ||

    (row.material_name || "")
      .toLowerCase()
      .includes(search)
  );

  // ---------------------------------------------------------
  // FILTER MATCH
  // ---------------------------------------------------------
  let matchesFilter = true;

  const allocated =
    Number(row.allocated_qty || 0);

  const available =
    Number(row.available_qty || 0);

  if (statusFilter === "PENDING") {

    matchesFilter = available > 0;

  } else if (
    statusFilter === "FULLY_ALLOCATED"
  ) {

    matchesFilter = available <= 0;

  } else if (
    statusFilter === "PARTIAL"
  ) {

    matchesFilter =
      allocated > 0 &&
      available > 0;
  }

  return (
    matchesSearch &&
    matchesFilter
  );
});
  
  // ===========================================================================
  // MAIN RENDER
  // ===========================================================================
  return (

    <div style={styles.overlay}>

      <div style={styles.drawer}>

        
        {/* ============================================================= */}
        {/* SEARCH */}
        {/* ============================================================= */}
        <div style={styles.searchContainer}>

          <input
            type="text"

            placeholder="Search material code or name..."

            value={searchText}

            onChange={(e) =>
              setSearchText(e.target.value)
            }

            style={styles.searchInput}
          />

        </div>
        
        {/* ============================================================= */}
        {/* FILTER CHIPS */}
        {/* ============================================================= */}
        <div style={styles.filterContainer}>

          {[
            "ALL",
            "PENDING",
            "PARTIAL",
            "FULLY_ALLOCATED"
          ].map((filter) => (

            <button
              key={filter}

              onClick={() =>
                setStatusFilter(filter)
              }

              style={{
                ...styles.filterChip,

                ...(statusFilter === filter
                  ? styles.filterChipActive
                  : {})
              }}
            >
              {filter.replaceAll("_", " ")}
            </button>

          ))}

        </div>
        {/* =================================================================== */}
        {/* HEADER */}
        {/* =================================================================== */}
        <div style={styles.header}>

          <div>

            <div style={styles.title}>
              Allocate BOM Materials
            </div>

            <div style={styles.subText}>

              {taskNode?.name}

            </div>

          </div>

          <button
            style={styles.closeBtn}
            onClick={onClose}
          >
            ✖
          </button>

        </div>

        {/* =================================================================== */}
        {/* BODY */}
        {/* =================================================================== */}
        <div style={styles.body}>

          {loading ? (

            <div style={styles.loader}>
              Loading BOM materials...
            </div>

          ) : bomMaterials.length === 0 ? (

            <div style={styles.emptyState}>
              No BOM materials found
            </div>

          ) : (

            <>

              {/* ============================================================= */}
              {/* HEADER */}
              {/* ============================================================= */}
              <div style={styles.gridHeader}>

                <div style={styles.colCheck}></div>

                <div style={styles.colMaterial}>
                  Material
                </div>

                <div style={styles.colQty}>
                BOM Qty
              </div>

              <div style={styles.colQty}>
                Allocated
              </div>

              <div style={styles.colQty}>
                Available
              </div>

              <div style={styles.colQty}>
                Planned Qty
              </div>

              </div>

              {/* ============================================================= */}
              {/* ROWS */}
              {/* ============================================================= */}
              <div style={styles.gridBody}>

                {filteredRows.map((row) => {

                  const selected =
                    selectedRows[row.bom_line_id];

                  return (

                    <div
                      key={row.bom_line_id}
                      style={styles.gridRow}
                    >

                      {/* ----------------------------------------------------- */}
                      {/* CHECKBOX */}
                      {/* ----------------------------------------------------- */}
                      <div style={styles.colCheck}>

                        <input
                          type="checkbox"

                          checked={!!selected}

                          onChange={() =>
                            toggleRow(row)
                          }
                        />

                      </div>

                      {/* ----------------------------------------------------- */}
                      {/* MATERIAL */}
                      {/* ----------------------------------------------------- */}
                      <div style={styles.colMaterial}>

                        <div style={styles.materialTitle}>

                          {row.material_code || "-"}

                          {" - "}

                          {row.material_name || "-"}

                        </div>

                        <div style={styles.materialSubText}>

                          UOM:
                          {" "}

                          {row.uom_code || "-"}

                        </div>

                      </div>

                      {/* ----------------------------------------------------- */}
                      {/* BOM QTY */}
                      {/* ----------------------------------------------------- */}
                      <div style={styles.colQty}>

                        {row.bom_qty || 0}

                      </div>
                      {/* ----------------------------------------------------- */}
                      {/* ALLOCATED QTY */}
                      {/* ----------------------------------------------------- */}
                      <div style={styles.colQty}>

                        {row.allocated_qty || 0}

                      </div>
                      {/* ----------------------------------------------------- */}
                      {/* AVAILABLE QTY */}
                      {/* ----------------------------------------------------- */}
                      <div style={styles.colQty}>

                        {row.available_qty || 0}

                      </div>

                      {/* ----------------------------------------------------- */}
                      {/* PLAN QTY */}
                      {/* ----------------------------------------------------- */}
                      <div style={styles.colQty}>

                        <input
                          type="number"

                          disabled={!selected}

                          value={
                            selected?.planned_qty || 0
                          }

                          onChange={(e) => {

                          const value = Number(
                            e.target.value || 0
                          );

                          if (value > row.available_qty) {

                            alert(
                              "Planned quantity cannot exceed available quantity"
                            );

                            return;
                          }

                          updateQty(
                            row.bom_line_id,
                            value
                          );
                        }}

                          style={styles.qtyInput}
                        />

                      </div>

                    </div>
                  );
                })}

              </div>

            </>

          )}

        </div>

        {/* =================================================================== */}
        {/* FOOTER */}
        {/* =================================================================== */}
        <div style={styles.footer}>

          <button
            style={styles.cancelBtn}
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            style={styles.allocateBtn}
            disabled={saving}
            onClick={handleAllocate}
          >
            {saving
              ? "Allocating..."
              : "Allocate Materials"}
          </button>

        </div>

      </div>

    </div>
  );
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  overlay: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    background: "rgba(0,0,0,0.4)",
    zIndex: 1000,
    display: "flex",
    justifyContent: "flex-end"
  },

  drawer: {
    width: "700px",
    background: "#fff",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxShadow: "-2px 0 8px rgba(0,0,0,0.2)"
  },

  header: {
    padding: "16px",
    borderBottom: "1px solid #eee",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },

  title: {
    fontSize: 18,
    fontWeight: 600
  },

  subText: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 4
  },

  closeBtn: {
    border: "none",
    background: "transparent",
    fontSize: 18,
    cursor: "pointer"
  },

  body: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column"
  },

  loader: {
    padding: 20,
    textAlign: "center"
  },

  emptyState: {
    padding: 30,
    textAlign: "center",
    color: "#6b7280"
  },

  gridHeader: {
    display: "flex",
    padding: "12px",
    borderBottom: "1px solid #ddd",
    background: "#f9fafb",
    fontWeight: 600,
    fontSize: 13
  },

  gridBody: {
    flex: 1,
    overflowY: "auto"
  },

  gridRow: {
    display: "flex",
    padding: "12px",
    borderBottom: "1px solid #f1f5f9",
    alignItems: "center"
  },

  colCheck: {
    width: 50,
    textAlign: "center"
  },

  colMaterial: {
    flex: 3
  },

  colQty: {
    flex: 1,
    textAlign: "center"
  },

  materialTitle: {
    fontWeight: 600,
    fontSize: 14
  },

  materialSubText: {
    fontSize: 11,
    color: "#6b7280",
    marginTop: 4
  },

  qtyInput: {
    width: 90,
    padding: "6px 8px",
    border: "1px solid #ccc",
    borderRadius: 6,
    textAlign: "center"
  },

  footer: {
    padding: "16px",
    borderTop: "1px solid #eee",
    display: "flex",
    justifyContent: "flex-end",
    gap: 10
  },

  cancelBtn: {
    padding: "8px 14px",
    border: "none",
    borderRadius: 6,
    background: "#e5e7eb",
    cursor: "pointer"
  },

  allocateBtn: {
    padding: "8px 16px",
    border: "none",
    borderRadius: 6,
    background: "#4f46e5",
    color: "#fff",
    cursor: "pointer"
  },
  searchContainer: {
  padding: "12px",
  borderBottom: "1px solid #eee"
},

searchInput: {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid #d1d5db",
  borderRadius: 8,
  fontSize: 14
},
filterContainer: {
  display: "flex",
  gap: 8,
  padding: "0 12px 12px 12px",
  flexWrap: "wrap"
},

filterChip: {
  padding: "6px 12px",
  borderRadius: 20,
  border: "1px solid #d1d5db",
  background: "#fff",
  cursor: "pointer",
  fontSize: 12
},

filterChipActive: {
  background: "#4f46e5",
  color: "#fff",
  border: "1px solid #4f46e5"
},
  
};